/**
 * @fileoverview Best-effort parser for single-line Australian addresses.
 *
 * The create-job wizard's AI extraction returns the registered office as a
 * single line (e.g. "Level 3, 100 King St, Sydney NSW 2000"); the intake
 * form stores street / suburb / state / postcode separately, so the line
 * has to be split client-side.
 *
 * MOR-999 — the original implementation took `street = parts[0]`, so any
 * address with a unit/level prefix ("Level 3, 100 King St, …") lost
 * everything after the first comma and users saw "only line one" of their
 * registered address. The street is now everything before the
 * suburb/state/postcode tail.
 */

export interface ParsedAustralianAddress {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
}

const STATE_PATTERN = /\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i;

/**
 * Parse an Australian address like "123 Example St, Sydney NSW 2000".
 * Returns as many components as can be inferred from the string; anything
 * that cannot be confidently split stays in `street` so no text is lost.
 */
export function parseAustralianAddress(
  address: string,
): ParsedAustralianAddress {
  const result = { street: "", suburb: "", state: "", postcode: "" };
  if (!address) return result;

  // Trailing postcode (4 digits).
  const postcodeMatch = address.match(/\b(\d{4})\s*$/);
  if (postcodeMatch) {
    result.postcode = postcodeMatch[1];
  }

  // State abbreviation anywhere in the line.
  const stateMatch = address.match(STATE_PATTERN);
  if (stateMatch) {
    result.state = stateMatch[1].toUpperCase();
  }

  const parts = address
    .split(/,/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    // The last part usually reads "Suburb STATE Postcode" — strip the
    // state/postcode to recover the suburb.
    const lastPart = parts[parts.length - 1];
    const suburbStr = lastPart
      .replace(STATE_PATTERN, "")
      .replace(/\b\d{4}\b/, "")
      .trim();
    if (suburbStr) {
      result.suburb = suburbStr;
      // Everything before the suburb part is the street — keeping
      // unit/level prefixes like "Level 3, 100 King St" intact (MOR-999).
      result.street = parts.slice(0, -1).join(", ");
    } else if (parts.length >= 3) {
      // The last part was only "STATE Postcode", so the suburb sits in its
      // own comma part: "100 King St, Sydney, NSW 2000".
      result.suburb = parts[parts.length - 2];
      result.street = parts.slice(0, -2).join(", ");
    } else {
      result.street = parts[0];
    }
  } else if (parts.length === 1) {
    // Single string without commas — try to split before the state.
    if (stateMatch && stateMatch.index !== undefined) {
      const beforeState = address.slice(0, stateMatch.index).trim();
      // Heuristic: the last word before the state is the suburb.
      const words = beforeState.split(/\s+/);
      if (words.length >= 3) {
        result.suburb = words[words.length - 1];
        result.street = words.slice(0, -1).join(" ");
      } else if (words.length === 2) {
        result.street = words[0];
        result.suburb = words[1];
      } else {
        result.street = beforeState;
      }
    } else {
      result.street = address;
    }
  }

  return result;
}
